import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface Apprentice {
  id: string;
  firstName: string;
  lastName: string;
  uln: string;
  standardCode: number;
  standardTitle: string;
  startDate: string;
  plannedEndDate: string;
}

interface ILRPreviewResponse {
  success: boolean;
  xml: string;
  apprentices?: Apprentice[];
  timestamp: string;
}

const ILRGeneration: React.FC = () => {
  const { user } = useAuth();
  const [ukprn, setUkprn] = useState('');
  const [academicYear, setAcademicYear] = useState('2425');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<ILRPreviewResponse | null>(null);
  const [showXML, setShowXML] = useState(false);

  const generateAcademicYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = -2; i <= 2; i++) {
      const year = currentYear + i;
      const nextYear = year + 1;
      const yearCode = year.toString().slice(2) + nextYear.toString().slice(2);
      years.push({
        code: yearCode,
        label: year + '/' + nextYear
      });
    }
    return years;
  };

  const academicYears = generateAcademicYears();

  const handlePreview = async () => {
    if (!ukprn || ukprn.length !== 8) {
      setError('Please enter a valid 8-digit UKPRN');
      return;
    }

    setLoading(true);
    setError(null);
    setPreviewData(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const tenantId = user?.tenant_id;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(
        apiUrl + '/api/ilr/preview?tenantId=' + tenantId + '&academicYear=' + academicYear + '&ukprn=' + ukprn,
        {
          headers: {
            'Authorization': 'Bearer ' + token
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate ILR preview');
      }

      const data = await response.json();
      setPreviewData(data);
      setShowXML(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!previewData?.xml) return;

    const blob = new Blob([previewData.xml], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dateStr = new Date().toISOString().split('T')[0];
    a.download = 'ILR_' + ukprn + '_' + academicYear + '_' + dateStr + '.xml';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const parseApprenticesFromXML = (xml: string): Apprentice[] => {
    const learners: Apprentice[] = [];
    const learnerMatches = Array.from(xml.matchAll(/<Learner>(.*?)<\/Learner>/gs));
    
    for (const match of learnerMatches) {
      const learnerXML = match[1];
      const idMatch = learnerXML.match(/<LearnRefNumber>(.*?)<\/LearnRefNumber>/);
      const firstNameMatch = learnerXML.match(/<GivenNames>(.*?)<\/GivenNames>/);
      const lastNameMatch = learnerXML.match(/<FamilyName>(.*?)<\/FamilyName>/);
      const ulnMatch = learnerXML.match(/<ULN>(.*?)<\/ULN>/);
      const standardCodeMatch = learnerXML.match(/<StdCode>(.*?)<\/StdCode>/);
      const startDateMatch = learnerXML.match(/<LearnStartDate>(.*?)<\/LearnStartDate>/);
      const endDateMatch = learnerXML.match(/<LearnPlanEndDate>(.*?)<\/LearnPlanEndDate>/);

      const id = idMatch ? idMatch[1] : '';
      const firstName = firstNameMatch ? firstNameMatch[1] : '';
      const lastName = lastNameMatch ? lastNameMatch[1] : '';
      const uln = ulnMatch ? ulnMatch[1] : '';
      const standardCode = standardCodeMatch ? parseInt(standardCodeMatch[1]) : 0;
      const startDate = startDateMatch ? startDateMatch[1] : '';
      const plannedEndDate = endDateMatch ? endDateMatch[1] : '';

      learners.push({
        id,
        firstName,
        lastName,
        uln,
        standardCode,
        standardTitle: 'Standard ' + standardCode,
        startDate,
        plannedEndDate
      });
    }
    
    return learners;
  };

  const apprentices = previewData?.xml ? parseApprenticesFromXML(previewData.xml) : [];

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">ILR Generation</h1>
        <p className="text-gray-600">Generate Individualised Learner Record (ILR) XML files for government funding submissions</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Generation Parameters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              UKPRN (UK Provider Reference Number)
            </label>
            <input
              type="text"
              value={ukprn}
              onChange={(e) => setUkprn(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="12345678"
              maxLength={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">8-digit provider reference number</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Academic Year
            </label>
            <select
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {academicYears.map(year => (
                <option key={year.code} value={year.code}>
                  {year.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handlePreview}
            disabled={loading || !ukprn}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
          >
            {loading ? 'Generating...' : 'Generate Preview'}
          </button>

          {previewData && (
            <button
              onClick={handleDownload}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
            >
              Download XML
            </button>
          )}
        </div>
      </div>

      {previewData && (
        <>
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              Included Apprentices ({apprentices.length})
            </h2>
            
            {apprentices.length === 0 ? (
              <p className="text-gray-500">No apprentices found for this academic year</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ULN</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Standard</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {apprentices.map(apprentice => (
                      <tr key={apprentice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {apprentice.firstName} {apprentice.lastName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {apprentice.uln}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {apprentice.standardCode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(apprentice.startDate).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(apprentice.plannedEndDate).toLocaleDateString('en-GB')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">XML Preview</h2>
              <button
                onClick={() => setShowXML(!showXML)}
                className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                {showXML ? 'Hide XML' : 'Show XML'}
              </button>
            </div>

            {showXML && (
              <div className="bg-gray-900 rounded-md p-4 overflow-x-auto">
                <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap">
                  {previewData.xml}
                </pre>
              </div>
            )}

            <div className="mt-4 text-sm text-gray-500">
              <p>Generated: {new Date(previewData.timestamp).toLocaleString('en-GB')}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ILRGeneration;
